import { useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { TaskItem } from '@demind-inc/core';

import { useAuthContext, useTaskActions } from '../data-access';
import { trackEventMixpanel } from '../utils';
import { getDueDateYYYYMMDD } from '../helpers';
import { normalizeRecurringString } from '../components';

export interface MoveTaskParams {
  task: TaskItem;
  targetDate: Dayjs;
  wasOverdue?: boolean;
  sortIndex?: number;
}

export const useMoveTask = () => {
  const { user } = useAuthContext();
  const { updateTodoTask, isUpdatingTask: isMovingTask } = useTaskActions();

  const handleMoveTask = useCallback(
    async ({ task, targetDate, wasOverdue = false, sortIndex }: MoveTaskParams) => {
      if (!user.userId) {
        return;
      }
      const hasStartDate = !!task.startDateTime?.datetime;
      const hasDueDateTime = !!task.dueDateTime?.datetime;
      const currentDueDate = getDueDateYYYYMMDD(task.dueDateTime);
      const isSameDate = currentDueDate
        ? currentDueDate === targetDate.format('YYYY-MM-DD')
        : false;

      let targetStartDatetime = targetDate;

      if (hasStartDate) {
        const prevStartDate = dayjs(task.startDateTime.datetime);
        const startDateHours = prevStartDate.hour();
        const startDateMinutes = prevStartDate.minute();
        targetStartDatetime = targetDate.hour(startDateHours).minute(startDateMinutes);
      }

      try {
        const recurringText = normalizeRecurringString(task.recurring);
        await updateTodoTask({
          boardId: task.boardId,
          taskId: task.taskId,
          newTaskInfo: {
            ...task,
            ...(recurringText
              ? {
                  recurring: {
                    ...task.recurring,
                    string: recurringText,
                    isRecurring: true,
                    lang: task.recurring?.lang || 'en',
                    timezone: task.recurring?.timezone || dayjs.tz.guess(),
                  },
                }
              : {}),
            ...(hasStartDate
              ? {
                  startDateTime: {
                    datetime: targetStartDatetime.toISOString(),
                    date: targetStartDatetime.format('YYYY-MM-DD'),
                    timezone: dayjs.tz.guess(),
                  },
                }
              : {}),
            dueDateTime: {
              ...(hasDueDateTime && isSameDate
                ? {
                    datetime: task.dueDateTime.datetime,
                  }
                : {}),
              date: targetDate.format('YYYY-MM-DD'),
              timezone: dayjs.tz.guess(),
            },
            appFrom: task.appFrom,
            ...(sortIndex !== undefined ? { sortIndex: sortIndex } : {}),
          },
          movedTaskParam: {
            prevDate:
              task.dueDateTime?.datetime || task.dueDateTime?.date
                ? dayjs(task.dueDateTime.datetime || task.dueDateTime.date).format('YYYY-MM-DD')
                : undefined,
            newDate: targetDate.format('YYYY-MM-DD'),
            wasOverdue,
          },
          shouldReflectToEvent: true,
        });
        trackEventMixpanel('move_date_of_task');
      } catch (error) {
        console.error('Failed to update task:', error);
      }
    },
    [user.userId]
  );

  return { handleMoveTask, isMovingTask };
};